import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/design-system.css';
import '../styles/landing.css';
import '../styles/landing-redesign.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeQuantities, setActiveQuantities] = useState([]);
  const [telemetryItems, setTelemetryItems] = useState([]);
  const [scanProgress, setScanProgress] = useState(0);
  const [bidTotal, setBidTotal] = useState(0);
  const heroRef = useRef(null);
  const manifestoRef = useRef(null);
  const workflowRefs = useRef([]);

  // Simulated live quantity detection
  useEffect(() => {
    const quantities = [
      { label: 'Linear FT', value: 1240, unit: 'LF', category: 'Curb & Gutter' },
      { label: 'CY Earthwork', value: 4830, unit: 'CY', category: 'Excavation' },
      { label: 'Storm Pipe 18"', value: 340, unit: 'LF', category: 'Utilities' },
      { label: 'Asphalt 3"', value: 2150, unit: 'SY', category: 'Paving' }
    ];

    let index = 0;
    const interval = setInterval(() => {
      if (index < quantities.length) {
        setActiveQuantities(prev => [...prev, quantities[index]]);
        index++;
      } else {
        clearInterval(interval);
      }
    }, 800);

    return () => clearInterval(interval);
  }, []);

  // Telemetry feed simulation
  useEffect(() => {
    const items = [
      'Detected: Storm Pipe 18" – 340 LF',
      'Detected: Curb & Gutter – 1,820 LF',
      'Detected: Concrete Sidewalk 4" – 980 SY',
      'Detected: Catch Basin Type 1 – 12 EA',
      'Detected: Asphalt Overlay 3" – 2,150 SY',
      'Detected: Earthwork Cut – 4,830 CY'
    ];

    let index = 0;
    const interval = setInterval(() => {
      if (index < items.length) {
        setTelemetryItems(prev => [...prev, items[index]]);
        index++;
      }
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Scan line animation
  useEffect(() => {
    const interval = setInterval(() => {
      setScanProgress(prev => (prev >= 100 ? 0 : prev + 2));
    }, 50);

    return () => clearInterval(interval);
  }, []);

  // Bid total count-up animation
  useEffect(() => {
    const targetTotal = 487250;
    const duration = 2000;
    const steps = 60;
    const increment = targetTotal / steps;
    let current = 0;
    let step = 0;

    const interval = setInterval(() => {
      if (step < steps) {
        current += increment;
        setBidTotal(Math.floor(current));
        step++;
      } else {
        setBidTotal(targetTotal);
        clearInterval(interval);
      }
    }, duration / steps);

    return () => clearInterval(interval);
  }, []);

  // Scroll-triggered manifesto reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('manifesto-revealed');
          }
        });
      },
      { threshold: 0.3 }
    );

    if (manifestoRef.current) {
      observer.observe(manifestoRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Workflow cards - no scroll effects

  return (
    <div className="landing-page landing-redesign">
      {/* 
          NAVBAR - Command Bar
           */}
      <nav className="command-bar-redesign">
        <div className="nav-grid-overlay"></div>
        <div className="container-wide">
          <div className="command-bar-content">
            <div className="brand-redesign">
              <div className="brand-logo-redesign">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <rect x="4" y="4" width="24" height="24" stroke="currentColor" strokeWidth="2.5"/>
                  <path d="M4 14h24M14 4v24" stroke="currentColor" strokeWidth="2.5"/>
                  <circle cx="16" cy="16" r="3" fill="currentColor"/>
                </svg>
              </div>
              <div className="brand-text-group">
                <span className="brand-name-redesign">CIVILY</span>
                <span className="brand-tagline">ESTIMATION ENGINE</span>
              </div>
            </div>

            <div className="nav-links-redesign">
              <a href="/" className="nav-link-redesign"><span className="nav-number">01</span>Home</a>
              <a href="/about" className="nav-link-redesign"><span className="nav-number">02</span>About</a>
              <a href="/services" className="nav-link-redesign"><span className="nav-number">03</span>Services</a>
              <a href="/contact" className="nav-link-redesign"><span className="nav-number">04</span>Contact</a>
              <a href="/docs" className="nav-link-redesign"><span className="nav-number">05</span>Docs</a>
            </div>

            <div className="nav-actions">
              <button className="btn-minimal" onClick={() => navigate('/app')}>
                <span className="btn-text">Login</span>
                <span className="btn-arrow">→</span>
              </button>
              <a href="mailto:contact@civilyapp.com" className="btn-primary-redesign">
                <span className="btn-text">Start Project</span>
                <span className="btn-icon">⊞</span>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* 
          HERO - Dashboard Landing
          */}
      <section className="hero-redesign" ref={heroRef}>
        <div className="hero-blueprint-grid"></div>
        <div className="hero-construction-lines">
          <div className="construction-line line-1"></div>
          <div className="construction-line line-2"></div>
          <div className="construction-line line-3"></div>
        </div>
        
        <div className="container-wide">
          <div className="hero-layout">
            <div className="hero-content-redesign">
              <div className="hero-label animate-slide-in-left">
                <span className="label-line"></span>
                <span className="label-text">QUANTIS v1.0 EXTRACTION ENGINE</span>
              </div>
              
              <h1 className="hero-headline-redesign animate-fade-in-up">
                <span className="headline-main">PRECISION</span>
                <span className="headline-sub">TAKEOFF</span>
                <span className="headline-accent">AUTOMATED</span>
              </h1>
              
              <p className="hero-description animate-fade-in-up delay-100">
                Industrial-grade AI extraction for construction estimators.
                <br/>
                Transform blueprints into bid-ready quantities in minutes.
              </p>

              <div className="hero-metrics animate-fade-in-up delay-200">
                <div className="metric-item">
                  <div className="metric-value">95%+</div>
                  <div className="metric-label">Accuracy</div>
                </div>
                <div className="metric-divider"></div>
                <div className="metric-item">
                  <div className="metric-value">&lt;5min</div>
                  <div className="metric-label">Processing</div>
                </div>
                <div className="metric-divider"></div>
                <div className="metric-item">
                  <div className="metric-value">24/7</div>
                  <div className="metric-label">Uptime</div>
                </div>
              </div>

              <div className="hero-cta-redesign animate-fade-in-up delay-300">
                <a href="mailto:contact@civilyapp.com" className="btn-hero-primary">
                  <span className="btn-bg"></span>
                  <span className="btn-content">
                    <span className="btn-text">Request Demo</span>
                    <span className="btn-arrow">→</span>
                  </span>
                </a>
                <button className="btn-hero-secondary" onClick={() => navigate('/app')}>
                  <span className="btn-content">
                    <span className="btn-text">View Platform</span>
                    <span className="btn-arrow">↗</span>
                  </span>
                </button>
              </div>
            </div>

            {/* Live Extraction Visualization */}
            <div className="hero-visualization animate-fade-in delay-400">
              <div className="viz-header">
                <div className="viz-title">
                  <span className="viz-icon">⊞</span>
                  <span className="viz-text">LIVE EXTRACTION</span>
                </div>
                <div className="viz-status">
                  <span className="status-pulse"></span>
                  <span className="status-label">PROCESSING</span>
                </div>
              </div>
              
              <div className="viz-blueprint">
                <div className="blueprint-scan-line" style={{ top: `${scanProgress}%` }}></div>
                <div className="blueprint-grid-pattern"></div>
                
                {activeQuantities.filter(qty => qty).map((qty, index) => (
                  <div 
                    key={index}
                    className="quantity-marker animate-pop-in"
                    style={{
                      top: `${15 + (index * 20)}%`,
                      left: `${10 + (index % 2) * 50}%`,
                      animationDelay: `${index * 0.2}s`
                    }}
                  >
                    <div className="marker-dot"></div>
                    <div className="marker-label">
                      <span className="marker-category">{qty.category}</span>
                      <span className="marker-value">{qty.value.toLocaleString()} {qty.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="viz-footer">
                <div className="viz-stat">
                  <span className="stat-label">Items Detected</span>
                  <span className="stat-value">{activeQuantities.length * 15}</span>
                </div>
                <div className="viz-stat">
                  <span className="stat-label">Confidence</span>
                  <span className="stat-value">94.2%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 
          CORE FEATURES - Functional Dashboard Artifacts
          */}
      <section className="features section-lg blueprint-grid">
        <div className="container">
          <div className="features-grid">
            
            {/* Card 1 - Auto-Extraction Preview */}
            <div className="feature-card card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <path d="M9 9h6M9 13h6M9 17h3"/>
                </svg>
              </div>
              <h3 className="feature-title">Intelligent Takeoff Engine</h3>
              <p className="feature-description">
                Quantis v1 scans construction plans and automatically extracts quantities with 95%+ accuracy
              </p>
              
              <div className="extraction-preview">
                <div className="blueprint-mockup">
                  <div className="scan-line" style={{ top: `${scanProgress}%` }}></div>
                  {activeQuantities.map((qty, index) => qty ? (
                    <div 
                      key={index}
                      className="quantity-highlight animate-fade-in"
                      style={{
                        top: `${20 + (index * 18)}%`,
                        left: `${15 + (index % 2) * 40}%`
                      }}
                    >
                      <span className="qty-label text-mono">{qty.label || 'Item'}: {(qty.value || 0).toLocaleString()}</span>
                    </div>
                  ) : null)}
                </div>
              </div>
            </div>

            {/* Card 2 - Quantity Intelligence Feed */}
            <div className="feature-card card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <h3 className="feature-title">Quantity Intelligence Feed</h3>
              <p className="feature-description">
                Real-time detection stream shows items as they're identified from your plans
              </p>
              
              <div className="telemetry-feed">
                {telemetryItems.map((item, index) => (
                  <div key={index} className="telemetry-item animate-slide-in-right text-mono">
                    {item}
                  </div>
                ))}
                {telemetryItems.length < 6 && (
                  <div className="telemetry-item processing">
                    <span className="cursor-blink">▊</span>
                    <span className="processing-badge">Processing...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Card 3 - Bid Breakdown Estimator */}
            <div className="feature-card card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"/>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              </div>
              <h3 className="feature-title">Bid Breakdown Estimator</h3>
              <p className="feature-description">
                Instant cost summaries with category breakdowns and profit calculations
              </p>
              
              <div className="bid-breakdown">
                <div className="bid-total text-mono">
                  ${bidTotal.toLocaleString()}
                </div>
                <div className="cost-bars">
                  <div className="cost-bar-item">
                    <div className="bar-label">
                      <span>Earthwork</span>
                      <span className="text-mono">$142,500</span>
                    </div>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: '29%', background: '#2C5F8A' }}></div>
                    </div>
                  </div>
                  <div className="cost-bar-item">
                    <div className="bar-label">
                      <span>Utilities</span>
                      <span className="text-mono">$168,200</span>
                    </div>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: '35%', background: '#E8541A' }}></div>
                    </div>
                  </div>
                  <div className="cost-bar-item">
                    <div className="bar-label">
                      <span>Paving</span>
                      <span className="text-mono">$121,800</span>
                    </div>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: '25%', background: '#2A7D4F' }}></div>
                    </div>
                  </div>
                  <div className="cost-bar-item">
                    <div className="bar-label">
                      <span>Structures</span>
                      <span className="text-mono">$54,750</span>
                    </div>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: '11%', background: '#F59E0B' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 
          PHILOSOPHY / MANIFESTO
          */}
      <section className="manifesto section-lg" ref={manifestoRef}>
        <div className="manifesto-bg blueprint-grid-dark"></div>
        <div className="container">
          <div className="manifesto-content">
            <p className="manifesto-old">Manual takeoffs take days.</p>
            <h2 className="manifesto-new">Ours take minutes.</h2>
          </div>
        </div>
      </section>

      {/* 
          WORKFLOW STEPS - Sticky Stacking Cards
          */}
      <section className="workflow">
        <div className="workflow-container">
          
          <div 
            className="workflow-card workflow-card-1"
            ref={el => workflowRefs.current[0] = el}
          >
            <div className="workflow-icon workflow-icon-orange">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            <div className="workflow-content">
              <div className="workflow-step">Step 1</div>
              <h3 className="workflow-title">Upload Your Plans</h3>
              <p className="workflow-description">
                Drag and drop PDF construction plans. Supports site plans, grading plans, utility plans, and more.
              </p>
              <div className="workflow-stats">
                <div className="workflow-stat">
                  <span className="stat-number text-mono">500MB+</span>
                  <span className="stat-label">File Size Support</span>
                </div>
                <div className="workflow-stat">
                  <span className="stat-number text-mono">&lt;30s</span>
                  <span className="stat-label">Upload Time</span>
                </div>
              </div>
            </div>
          </div>

          <div 
            className="workflow-card workflow-card-2"
            ref={el => workflowRefs.current[1] = el}
          >
            <div className="workflow-icon workflow-icon-blue">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v6m0 6v6m-9-9h6m6 0h6"/>
              </svg>
            </div>
            <div className="workflow-content">
              <div className="workflow-step">Step 2</div>
              <h3 className="workflow-title">Quantis v1 Extracts Quantities</h3>
              <p className="workflow-description">
                Quantis v1 analyzes every page, identifying line items, measurements, and specifications automatically.
              </p>
              <div className="workflow-stats">
                <div className="workflow-stat">
                  <span className="stat-number text-mono">95%+</span>
                  <span className="stat-label">Accuracy Rate</span>
                </div>
                <div className="workflow-stat">
                  <span className="stat-number text-mono">24/7</span>
                  <span className="stat-label">Processing</span>
                </div>
              </div>
            </div>
          </div>

          <div 
            className="workflow-card workflow-card-3"
            ref={el => workflowRefs.current[2] = el}
          >
            <div className="workflow-icon workflow-icon-green">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div className="workflow-content">
              <div className="workflow-step">Step 3</div>
              <h3 className="workflow-title">Export Your Estimate</h3>
              <p className="workflow-description">
                Download professional bid summaries in Excel, CSV, or PDF with annotations. Ready for client delivery.
              </p>
              <div className="workflow-stats">
                <div className="workflow-stat">
                  <span className="stat-number text-mono">3</span>
                  <span className="stat-label">Export Formats</span>
                </div>
                <div className="workflow-stat">
                  <span className="stat-number text-mono">Instant</span>
                  <span className="stat-label">Generation</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/*
          PRICING
       */}
      <section className="pricing section-lg blueprint-grid" id="pricing">
        <div className="container">
          <div className="pricing-header">
            <h2 className="heading-xl">Built for Scale</h2>
            <p className="pricing-subtitle">From solo estimators to enterprise teams</p>
          </div>

          <div className="pricing-grid">
            
            <div className="pricing-card card">
              <div className="pricing-tier">Starter</div>
              <div className="pricing-price">
                <span className="price-amount text-mono">$299</span>
                <span className="price-period">/month</span>
              </div>
              <ul className="pricing-features">
                <li>50 pages/month</li>
                <li>Quantis v1 extraction</li>
                <li>Excel/CSV export</li>
                <li>Email support</li>
              </ul>
              <button className="btn btn-outline" style={{ width: '100%' }}>
                Start Trial
              </button>
            </div>

            <div className="pricing-card card pricing-card-featured">
              <div className="pricing-badge">Most Popular</div>
              <div className="pricing-tier">Professional</div>
              <div className="pricing-price">
                <span className="price-amount text-mono">$899</span>
                <span className="price-period">/month</span>
              </div>
              <ul className="pricing-features">
                <li>500 pages/month</li>
                <li>Advanced Quantis v1 extraction</li>
                <li>All export formats</li>
                <li>Priority support</li>
                <li>Team collaboration</li>
              </ul>
              <button className="btn btn-primary" style={{ width: '100%' }}>
                Start Free Trial
              </button>
            </div>

            <div className="pricing-card card">
              <div className="pricing-tier">Enterprise</div>
              <div className="pricing-price">
                <span className="price-amount text-mono">Custom</span>
              </div>
              <ul className="pricing-features">
                <li>Unlimited pages</li>
                <li>Dedicated AI models</li>
                <li>API access</li>
                <li>White-label options</li>
                <li>SLA guarantee</li>
                <li>Custom integrations</li>
              </ul>
              <button className="btn btn-secondary" style={{ width: '100%' }}>
                Contact Sales
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* 
          FOOTER
          */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="brand-logo">
                <span className="brand-icon">C</span>
              </div>
              <span className="brand-name">Civily</span>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-heading">Product</h4>
                <a href="/services">Features</a>
                <a href="/services">Pricing</a>
                <a href="/login">Login</a>
              </div>
              <div className="footer-column">
                <h4 className="footer-heading">Company</h4>
                <a href="/about">About</a>
                <a href="/contact">Contact</a>
              </div>
              <div className="footer-column">
                <h4 className="footer-heading">Resources</h4>
                <a href="/docs">Documentation</a>
                <a href="mailto:support@civilyapp.com">Support</a>
              </div>
            </div>

            <div className="footer-status">
              <div className="status-indicator">
                <span className="status-dot status-operational"></span>
                <span className="status-text">System Status: Operational</span>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2026 Civily by Civio. Built for estimators.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
